import * as Dialog from "@radix-ui/react-dialog";
import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";
import { t } from "../i18n/t";
import { cx } from "./klasy";
import { IkonaX } from "./ikony";
import type { KomponentIkony } from "./ikony";
import { PoleTekstowe } from "./Pola";
import { Przycisk } from "./Przyciski";
import "../styles/wspolne.css";

export type LiniaProduktu = "wedzenie" | "destylacja";

/** Linia karty pulpitu (TrescKarty) - okna i menu w portalu biorą z niej kolor, gdy nie podano `linia`. */
export const LiniaKarty = createContext<LiniaProduktu | undefined>(undefined);

export interface OknoProps {
  otwarte: boolean;
  onZmianaOtwarcia: (otwarte: boolean) => void;
  tytul: string;
  /** Krótki opis pod tytułem (łączony z oknem przez aria-describedby) */
  opis?: ReactNode;
  /** Na telefonie każde okno wysuwa się od dołu (arkusz), od tabletu - na środku */
  szerokie?: boolean;
  /** Duża ikona nad tytułem (okna potwierdzeń) */
  ikona?: KomponentIkony;
  ikonaRodzaj?: "akcent" | "alarm" | "ok";
  /** Przyciski na dole okna */
  akcje?: ReactNode;
  /** Kolor linii - okno renderuje się w portalu, więc nie dziedziczy go ze strony (domyślnie z karty: LiniaKarty) */
  linia?: LiniaProduktu;
  /** Element otwierający (opakowany w Dialog.Trigger asChild) - opcjonalny, gdy okno sterowane z zewnątrz */
  wyzwalacz?: ReactNode;
  children?: ReactNode;
}

/** Okno dialogowe na prymitywach @radix-ui/react-dialog (fokus, Esc, blokada przewijania - z Radix). */
export function Okno({ otwarte, onZmianaOtwarcia, tytul, opis, szerokie, ikona: Ikona, ikonaRodzaj = "akcent", akcje, linia, wyzwalacz, children }: OknoProps) {
  const liniaKarty = useContext(LiniaKarty);
  return (
    <Dialog.Root open={otwarte} onOpenChange={onZmianaOtwarcia}>
      {wyzwalacz && <Dialog.Trigger asChild>{wyzwalacz}</Dialog.Trigger>}
      <Dialog.Portal>
        <Dialog.Overlay className="iot-zaslona" />
        <Dialog.Content
          className={cx("iot-okno", "iot-portal", szerokie && "iot-okno--szerokie")}
          data-iot-linia={linia ?? liniaKarty}
        >
          <div className="iot-okno__uchwyt" aria-hidden="true" />
          {Ikona && <div className={cx("iot-okno__ikona", ikonaRodzaj !== "akcent" && `iot-okno__ikona--${ikonaRodzaj}`)}><Ikona /></div>}
          <div className="iot-okno__naglowek">
            <Dialog.Title className="iot-okno__tytul">{tytul}</Dialog.Title>
            {!Ikona && (
              <Dialog.Close asChild>
                <button type="button" className="iot-btn-ikona iot-okno__zamknij" aria-label={t("Zamknij")}><IkonaX /></button>
              </Dialog.Close>
            )}
          </div>
          {opis ? <Dialog.Description className="iot-okno__opis">{opis}</Dialog.Description> : <Dialog.Description className="iot-sr">{tytul}</Dialog.Description>}
          {children}
          {akcje && <div className="iot-okno__akcje">{akcje}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface PotwierdzenieProps {
  otwarte: boolean;
  onZmianaOtwarcia: (otwarte: boolean) => void;
  tytul: string;
  tresc: ReactNode;
  etykietaPotwierdz: string;
  etykietaAnuluj?: string;
  onPotwierdz: () => void;
  /** Czerwony przycisk - dla usuwania, kończenia, cofania dostępu */
  niebezpieczne?: boolean;
  ikona?: KomponentIkony;
  /** Trwa wykonywanie akcji - przycisk pokazuje ładowanie */
  ladowanie?: boolean;
  linia?: LiniaProduktu;
  /** Dodatkowa treść pod opisem */
  children?: ReactNode;
  potwierdzWylaczone?: boolean;
  /** Przycisk działa dopiero po wpisaniu tego tekstu (np. nazwy urządzenia przy ustawieniach fabrycznych) */
  wpisz?: string;
}

/** Okno „Na pewno?” z dwoma przyciskami. Anuluj zamyka okno. */
export function Potwierdzenie({ otwarte, onZmianaOtwarcia, tytul, tresc, etykietaPotwierdz, etykietaAnuluj, onPotwierdz, niebezpieczne, ikona, ladowanie, linia, children, potwierdzWylaczone, wpisz }: PotwierdzenieProps) {
  const [wpisane, ustawWpisane] = useState("");
  const zmiana = (o: boolean) => {
    if (!o) ustawWpisane("");
    onZmianaOtwarcia(o);
  };
  const niewpisane = !!wpisz && wpisane.trim() !== wpisz.trim();
  return (
    <Okno
      otwarte={otwarte}
      onZmianaOtwarcia={zmiana}
      tytul={tytul}
      opis={tresc}
      ikona={ikona}
      ikonaRodzaj={niebezpieczne ? "alarm" : "akcent"}
      linia={linia}
      akcje={
        <>
          <Dialog.Close asChild>
            <Przycisk wariant="cichy">{etykietaAnuluj ?? t("Anuluj")}</Przycisk>
          </Dialog.Close>
          <Przycisk wariant={niebezpieczne ? "niebezpieczny-pelny" : "glowny"} onClick={() => { ustawWpisane(""); onPotwierdz(); }}
            ladowanie={ladowanie} disabled={potwierdzWylaczone || niewpisane} data-test="potwierdz">
            {etykietaPotwierdz}
          </Przycisk>
        </>
      }
    >
      {wpisz && (
        <PoleTekstowe etykieta={t("Wpisz „{n}”, aby potwierdzić:", { n: wpisz })} wartosc={wpisane} onZmien={ustawWpisane}
          autoComplete="off" data-test="potwierdz-wpisz" />
      )}
      {children}
    </Okno>
  );
}

/** Przycisk zamykający najbliższe okno - do własnych stopek okien */
export function ZamknijOkno({ children }: { children: ReactNode }) {
  return <Dialog.Close asChild>{children}</Dialog.Close>;
}
